import { Directive, HostListener, Input } from '@angular/core';
import { NgControl } from '@angular/forms';
import { DecimalFraction, Utils } from 'src/app/helpers/utils';

@Directive({
  standalone: false,
  selector: '[appLocaleNumberInput]'
})
export class LocaleNumberDirective {
  @Input() maxDecimals: DecimalFraction = DecimalFraction.TWO;
  @Input() allowNegative = false;

  constructor(private ngControl: NgControl, private utils: Utils) { }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    const inputValue = input.value;

    const { key, ctrlKey, metaKey } = event;

    const useComma = this.utils.commaCurrFormat.includes(this.utils.getLocale());
    const decimalSeparator = useComma ? ',' : '.';

    // Allow navigation & editing keys
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (allowedKeys.includes(key)) return;

    // Allow Ctrl/Command shortcuts
    if ((ctrlKey || metaKey) && ['a', 'c', 'v', 'x', 'z', 'y'].includes(key.toLowerCase())) {
      return;
    }

    // Allow digits, decimal separator, and optional minus sign
    const allowedCharRegex = this.allowNegative
      ? (useComma ? /[\d,-]/ : /[\d.-]/)
      : (useComma ? /[\d,]/ : /[\d.]/);

    if (!allowedCharRegex.test(key)) {
      event.preventDefault();
      return;
    }

    // Predict the next value
    const selectionStart = input.selectionStart ?? inputValue.length;
    const selectionEnd = input.selectionEnd ?? inputValue.length;

    const nextValue =
      inputValue.slice(0, selectionStart) + key + inputValue.slice(selectionEnd);

    // Prevent multiple decimals
    const regexMultipleDecimals = new RegExp(`\\${decimalSeparator}`, 'g');
    if ((nextValue.match(regexMultipleDecimals) || []).length > 1) {
      event.preventDefault();
      return;
    }

    // ✅ Restrict minus
    if (this.allowNegative) {
      const minusCount = (nextValue.match(/-/g) || []).length;
      if (minusCount > 1 || (minusCount === 1 && !nextValue.startsWith('-'))) {
        event.preventDefault();
        return;
      }
    }

    // Prevent leading zero without decimal (00, 01, -01 etc.)
    if (/^-?0\d+/.test(nextValue.replace(decimalSeparator, '.'))) {
      event.preventDefault();
      return;
    }

    // ✅ Restrict decimals
    const parts = nextValue.replace('-', '').split(decimalSeparator);
    if (parts[1] !== undefined && parts[1].length > this.maxDecimals) {
      event.preventDefault();
    }
  }
}
